import React, { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Modal } from './ui/Modal';
import OsScroll from './ui/OsScroll';
import {
  listQueryHistory,
  clearQueryHistory,
  removeQueryHistoryAt,
} from '../store/dbQueryHistory';

/**
 * Past statements, newest first.
 *
 * Scoped to the current connection by default: history from a different
 * database is noise at best and something you might run by accident at worst.
 */

function relativeTime(at, now) {
  const secs = Math.max(0, Math.round((now - at) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

export default function DbQueryHistoryModal({ open, connId, onPick, onClose }) {
  const [search, setSearch] = useState('');
  const [allConnections, setAllConnections] = useState(false);
  // Bumped after a destructive action to force a re-render, which re-reads the
  // store — a plain module rather than a subscribable one. The value itself is
  // never read, so it is not bound.
  const [, setRevision] = useState(0);

  if (!open) return null;

  // Read on render rather than memoised. Memoising either would need a
  // cache-busting dependency the value does not actually read, and neither is
  // worth it: the store is a plain module and the list is a filter over at most
  // a few hundred entries.
  const now = Date.now();
  const entries = listQueryHistory({ connId: allConnections ? null : connId, search });

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Query history"
      subtitle={`${entries.length} statement${entries.length === 1 ? '' : 's'}`}
      size="xl"
    >
      <div className="dbm-hist">
        <div className="dbm-hist-controls">
          <input
            type="text"
            className="dbm-hist-search"
            placeholder="Search statements"
            aria-label="Search query history"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <label className="dbm-hist-check">
            <input
              type="checkbox"
              className="checkbox-brand"
              checked={allConnections}
              onChange={(e) => setAllConnections(e.target.checked)}
            />
            All connections
          </label>
          <button
            type="button"
            className="dbm-pager-btn"
            disabled={entries.length === 0}
            onClick={() => {
              clearQueryHistory();
              setRevision((r) => r + 1);
            }}
          >
            Clear
          </button>
        </div>

        <OsScroll className="dbm-hist-list">
          {entries.length === 0 ? (
            <div className="dbm-hist-empty">
              {search ? `Nothing matches “${search}”` : 'Nothing has been run from this connection yet'}
            </div>
          ) : (
            entries.map((entry, i) => (
              <div key={`${entry.at}:${i}`} className="dbm-hist-row">
                <button
                  type="button"
                  className="dbm-hist-sql"
                  title="Load into the editor"
                  onClick={() => {
                    onPick(entry.sql);
                    onClose();
                  }}
                >
                  <code>{entry.sql}</code>
                  <span className="dbm-hist-meta">
                    <span>{relativeTime(entry.at, now)}</span>
                    {entry.durationMs != null && <span>{entry.durationMs} ms</span>}
                    {entry.rowCount != null && (
                      <span>
                        {entry.rowCount.toLocaleString()} row{entry.rowCount === 1 ? '' : 's'}
                      </span>
                    )}
                    {entry.runs > 1 && <span>×{entry.runs}</span>}
                    {entry.dbName && <span>{entry.dbName}</span>}
                    {entry.error && <span className="dbm-hist-failed">failed</span>}
                  </span>
                </button>
                <button
                  type="button"
                  className="dbm-hist-del"
                  title="Remove from history"
                  aria-label="Remove from history"
                  onClick={() => {
                    // The index into the filtered view is not the index into
                    // storage, so find the entry itself.
                    const all = listQueryHistory();
                    const idx = all.findIndex((e) => e.at === entry.at && e.sql === entry.sql);
                    if (idx !== -1) removeQueryHistoryAt(idx);
                    setRevision((r) => r + 1);
                  }}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))
          )}
        </OsScroll>
      </div>
    </Modal>
  );
}
